import { useEffect } from 'react';

/**
 * Mounts a global IntersectionObserver that adds .cin-visible to any
 * .cin-reveal element entering the viewport, and animates any
 * .cin-counter[data-cin-count] inside it.
 *
 * Pass a `key` (typically `pathname`) so the observer re-scans on route
 * changes — otherwise pages navigated to client-side will keep their
 * .cin-reveal elements at opacity 0 forever.
 */
export const useCinReveal = (key?: string) => {
  useEffect(() => {
    // The IntersectionObserver — created lazily on first scan
    let intersectionObserver: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let pendingRaf: number | null = null;

    const observeNew = () => {
      pendingRaf = null;
      if (!intersectionObserver) {
        intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              e.target.classList.add('cin-visible');
              const counters = e.target.classList.contains('cin-counter')
                ? [e.target as HTMLElement]
                : Array.from(e.target.querySelectorAll<HTMLElement>('.cin-counter'));
              counters.forEach(animateCounter);
              intersectionObserver?.unobserve(e.target);
            });
          },
          { threshold: 0.15, rootMargin: '0px 0px -80px 0px' }
        );
      }
      // Pick up any .cin-reveal elements not already visible / not yet observed.
      // observe() is idempotent — observing an already-observed element is a no-op.
      document
        .querySelectorAll<HTMLElement>('.cin-reveal:not(.cin-visible)')
        .forEach((el) => intersectionObserver!.observe(el));
    };

    // Coalesce many mutations within a single frame into one rescan.
    const schedule = () => {
      if (pendingRaf !== null) return;
      pendingRaf = requestAnimationFrame(observeNew);
    };

    // Initial scan after first paint.
    schedule();

    // Watch for new .cin-reveal elements added by React state updates
    // (filters, pagination, search results, route children mounting late, etc.).
    mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (pendingRaf !== null) cancelAnimationFrame(pendingRaf);
      mutationObserver?.disconnect();
      intersectionObserver?.disconnect();
    };
  }, [key]);
};

const animateCounter = (el: HTMLElement) => {
  const target = parseInt(el.dataset.cinCount ?? '', 10);
  if (isNaN(target)) return;
  const suffix = el.dataset.cinSuffix ?? '';
  // Group thousands (2,500) but never a year: 2018 must stay "2018" (2026-10-14).
  const formatVal = (v: number) => (v >= 1000 && !(target >= 1900 && target <= 2100) ? v.toLocaleString('he-IL') : String(v));
  if (typeof window !== 'undefined' && (window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.userAgent.includes('HeadlessChrome'))) {
    el.textContent = formatVal(target) + suffix;
    return;
  }
  const duration = 1400;
  const start = performance.now();
  const tick = (now: number) => {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    const val = Math.round(target * eased);
    el.textContent = formatVal(val) + suffix;
    if (p < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

/**
 * Magnetic effect for buttons — translates element toward cursor on hover.
 * Pass `key` (typically pathname) so it re-binds on route change.
 */
export const useCinMagnetic = (key?: string) => {
  useEffect(() => {
    let handlers: Array<{ el: HTMLElement; move: (e: MouseEvent) => void; leave: () => void }> = [];
    const id = requestAnimationFrame(() => {
      const els = document.querySelectorAll<HTMLElement>('.cin-magnetic');
      els.forEach((el) => {
        const move = (e: MouseEvent) => {
          const r = el.getBoundingClientRect();
          const x = (e.clientX - r.left - r.width / 2) * 0.18;
          const y = (e.clientY - r.top - r.height / 2) * 0.18;
          el.style.transform = `translate(${x}px, ${y}px)`;
        };
        const leave = () => {
          el.style.transform = '';
        };
        el.addEventListener('mousemove', move);
        el.addEventListener('mouseleave', leave);
        handlers.push({ el, move, leave });
      });
    });
    return () => {
      cancelAnimationFrame(id);
      handlers.forEach(({ el, move, leave }) => {
        el.removeEventListener('mousemove', move);
        el.removeEventListener('mouseleave', leave);
      });
    };
  }, [key]);
};

/**
 * Cursor spotlight follower. Adds a fixed div that follows the cursor.
 * Hidden on touch devices via CSS media query.
 */
export const useCinSpotlight = () => {
  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'cin-spotlight';
    document.body.appendChild(el);
    let raf: number | null = null;
    const move = (e: MouseEvent) => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.left = e.clientX + 'px';
        el.style.top = e.clientY + 'px';
      });
    };
    document.addEventListener('mousemove', move);
    return () => {
      document.removeEventListener('mousemove', move);
      el.remove();
    };
  }, []);
};

/**
 * Touch screens (2026-10-14): a pressed .cin-tilt card or .cin-magnetic button
 * turns slightly toward the finger and springs back on release — the mobile
 * stand-in for the mouse hover tilt/magnet. One delegated listener; a card
 * that runs its own useTilt (it sets inline transforms while pressed) is left
 * alone. Off for mouse pointers and with prefers-reduced-motion.
 */
export const useCinPressTilt = () => {
  useEffect(() => {
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let el: HTMLElement | null = null;
    const release = () => {
      if (!el) return;
      const t = el;
      el = null;
      t.style.transition = 'transform 420ms cubic-bezier(0.34,1.56,0.64,1)';
      t.style.transform = '';
      window.setTimeout(() => {
        if (t !== el) t.style.transition = '';
      }, 450);
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      const t = (e.target as HTMLElement | null)?.closest<HTMLElement>('.cin-tilt, .cin-magnetic');
      if (!t || t.dataset.ownTilt !== undefined) return;
      el = t;
      const r = t.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      const deg = t.classList.contains('cin-magnetic') ? 0 : 5;
      t.style.transition = 'transform 120ms cubic-bezier(0.23,1,0.32,1)';
      t.style.transform = `perspective(800px) rotateX(${(-py * deg).toFixed(2)}deg) rotateY(${(px * deg).toFixed(2)}deg) scale(0.97)`;
    };
    document.addEventListener('pointerdown', down, { passive: true });
    document.addEventListener('pointerup', release, { passive: true });
    document.addEventListener('pointercancel', release, { passive: true });
    window.addEventListener('scroll', release, { passive: true });
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', release);
      window.removeEventListener('scroll', release);
    };
  }, []);
};
